/* eslint-disable max-len */
import {
  Button,
  Collapse,
  Form,
  FormListFieldData,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd"
import Styles from "./styles.module.css"
import { PlusCircleOutlined } from "@ant-design/icons"
import { Notification } from "domains/specialEvents/pages/main"
import { NotificationItem } from "./components/NotificationItem/NotificationItem"
import { SpecialEventPresetsConfigType } from "domains/managingJson/store/types"
import { EventPresetButton } from "../eventPresetButton"
import {
  getPresetForNotification,
  hasExternalPixel,
  hasInternalPixel,
  isNotificationUsingPreset,
} from "domains/specialEvents/presets"
import { getGlobalImageSources } from "domains/specialEvents/normalization"
import { v4 as uuidv4 } from "uuid"
import { gameLabel } from "domains/specialEvents/timeBasedGames"

const { Text } = Typography

type Props = {
  parentIndex: number;
  contestMode?: boolean;
  presetsConfig?: SpecialEventPresetsConfigType;
  variant?: "full" | "compact";
};

const getGlobalImageCount = (notification?: Notification) => getGlobalImageSources(notification).filter(source => source.src).length

const renderNumberTag = (value: number | undefined, suffix: string) => (
  value
    ? <Tag>{value}{suffix}</Tag>
    : <Tag className={Styles.notSetTag}>Not set</Tag>
)

export const NotificationEditor = ({ parentIndex, contestMode, presetsConfig, variant = "full" }: Props) => {
  const form = Form.useFormInstance()
  const notifications = Form.useWatch(["notificationsPerGame", parentIndex, "notifications"], form) as Notification[] | undefined
  const gameId = Number(Form.useWatch(["notificationsPerGame", parentIndex, "gameId"], form)
    || form.getFieldValue(["notificationsPerGame", parentIndex, "gameId"]))
  const isCompact = variant === "compact" && !contestMode
  const getNotification = (notificationIndex: number): Notification | undefined => (
    notifications?.[notificationIndex] || form.getFieldValue([
      "notificationsPerGame",
      parentIndex,
      "notifications",
      notificationIndex,
    ])
  )
  const getNotificationPreset = (notificationIndex: number) => (
    presetsConfig
      ? getPresetForNotification(presetsConfig, gameId, getNotification(notificationIndex)?.triggerEventName)
      : undefined
  )

  return (
    <Form.List name={[parentIndex, "notifications"]}>
      {(fields, { add, remove }) => {
        const duplicate = (index: number) => {
          const notification: Notification = form.getFieldValue([
            "notificationsPerGame",
            parentIndex,
            "notifications",
            index,
          ])

          add({ ...notification, uid: uuidv4() })
        }

        const addWithSourceImageGeos = () => {
          const countries = contestMode
            ? form.getFieldValue(["countries"]) || []
            : form.getFieldValue(["restrictedToCountries"]) || []
          const countriesWithGlobal = ["global", ...countries]
          const notificationWithSourceImagesWithGeos = {
            uid: uuidv4(),
            sourceImagesByGeo: countriesWithGlobal.map(country => ({ country, imageSources: [] })),
          }
          add(notificationWithSourceImagesWithGeos)
        }

        const addButtons = (
          <Space wrap className={Styles.addActions}>
            {presetsConfig && (
              <EventPresetButton parentIndex={parentIndex} presetsConfig={presetsConfig} add={add} />
            )}
            <Button icon={<PlusCircleOutlined />} onClick={() => addWithSourceImageGeos()}>
              Add custom{" "}
              {gameLabel(form.getFieldValue(["notificationsPerGame", parentIndex, "gameId"]))}{" "}
              notification
            </Button>
          </Space>
        )

        const renderSummary = (notificationIndex: number) => {
          const notification = getNotification(notificationIndex)
          const preset = getNotificationPreset(notificationIndex)
          const isDefault = presetsConfig ? isNotificationUsingPreset(notification || {}, preset, presetsConfig) : false
          const imageCount = getGlobalImageCount(notification)
          const hasInternal = hasInternalPixel(notification?.trackingPixels)
          const hasExternal = hasExternalPixel(notification?.trackingPixels)

          return (
            <div className={Styles.compactHeader}>
              <Space direction="vertical" size={0} className={Styles.eventIdentity}>
                <Tooltip title={preset?.description}>
                  <Text strong>{preset?.friendlyName || notification?.triggerEventName || "Custom event"}</Text>
                </Tooltip>
                <Text code className={Styles.triggerKey}>{notification?.triggerEventName || "missing trigger"}</Text>
                {preset?.codeSupported === false && <Tag color="warning">Not shipped</Tag>}
              </Space>
              <Space wrap className={Styles.summaryTags}>
                {renderNumberTag(notification?.buffsReward, " Buffs")}
                {renderNumberTag(notification?.triggerDelay, "s delay")}
                {renderNumberTag(notification?.duration, "s duration")}
                <Tag color={imageCount ? "success" : "error"}>{imageCount ? "Asset ready" : "Missing asset"}</Tag>
                <Tag color={hasInternal ? "success" : "error"}>Internal pixel</Tag>
                <Tag color={hasExternal ? "success" : "default"}>Client pixel</Tag>
                <Tag color={isDefault ? "blue" : "gold"}>{isDefault ? "Default" : "Custom"}</Tag>
              </Space>
            </div>
          )
        }

        if (isCompact) {
          return (
            <Space direction="vertical" size="middle" className={Styles.mainSpace}>
              {addButtons}
              <Collapse className={Styles.compactList} bordered={false}>
                {fields.map(({ key, name: notificationIndex, ...restFields }) => (
                  <Collapse.Panel
                    key={key.toString()}
                    header={renderSummary(notificationIndex)}
                    extra={(
                      <div onClick={event => event.stopPropagation()}>
                        <Form.Item
                          noStyle
                          {...restFields}
                          name={[notificationIndex, "enabled"]}
                          valuePropName="checked"
                          initialValue={true}
                          getValueFromEvent={(checked: boolean) => (
                            getNotificationPreset(notificationIndex)?.codeSupported === false
                              ? false
                              : checked
                          )}
                        >
                          <Switch size="small" />
                        </Form.Item>
                      </div>
                    )}
                  >
                    <NotificationItem
                      key={key.toString()}
                      restFields={restFields as FormListFieldData}
                      notificationIndex={notificationIndex}
                      parentIndex={parentIndex}
                      duplicate={duplicate}
                      remove={remove}
                      contestMode={contestMode}
                      hideHeading
                      codeSupported={getNotificationPreset(notificationIndex)?.codeSupported}
                    />
                  </Collapse.Panel>
                ))}
              </Collapse>
            </Space>
          )
        }

        return (
          <Space direction="vertical" size="large" className={Styles.mainSpace}>
            {fields.map(({ key, name: notificationIndex, ...restFields }) => {
              return (
                <NotificationItem
                  key={key.toString()}
                  restFields={restFields as FormListFieldData}
                  notificationIndex={notificationIndex}
                  parentIndex={parentIndex}
                  duplicate={duplicate}
                  remove={remove}
                  contestMode={contestMode}
                  codeSupported={getNotificationPreset(notificationIndex)?.codeSupported}
                />
              )
            })}

            {addButtons}

          </Space>
        )
      }}
    </Form.List>
  )
}
